import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Button,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useApiSend, useApiGet } from '../../utils/httpClientTs';
import { updatePassword, getResetPassword } from '../../endpoints/login';
import AlertItem from '../../Components/AlertItem';
import { Image } from 'mui-image';
import logo from './../../assets/PEO_InformalLogo_Color.png';
import { ArrowBack, Visibility, VisibilityOff } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  PasswordResetConfirm,
  PasswordResetComplete,
  PasswordResetResponse,
} from '../../types/auth';

const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);

  console.log('Reset Password Token:', token);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const { data, isFetching } = useApiGet<any, PasswordResetConfirm>(
    ['getResetPassword', token],
    getResetPassword,
    {
      enabled: !!token,
      retry: 1,
    },
    { token: token || '' }
  );

  const { mutate } = useApiSend<PasswordResetResponse, PasswordResetComplete>(
    updatePassword,
    (data) => {
      setShowAlert(true);
      setIsSubmitting(false);
      setDisabled(true);
    },
    (error) => {
      // Handle error response
      setError('Error updating password, please try again later.');
      setIsSubmitting(false);
    }
  );

  const handleSubmit = (): void => {
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 12) {
      setError('Password must be at least 12 characters long');
      return;
    }
    if (password.length > 128) {
      setError('Password must be less than 128 characters long');
      return;
    }
    if (password.includes(' ')) {
      setError('Password cannot contain spaces');
      return;
    }
    if (password.includes('.')) {
      setError('Password cannot contain periods');
      return;
    }
    // check if password contains at least one uppercase letter
    if (!/[A-Z]/.test(password)) {
      setError('Password must contain at least one uppercase letter');
      return;
    }
    // check if password contains at least one lowercase letter
    if (!/[a-z]/.test(password)) {
      setError('Password must contain at least one lowercase letter');
      return;
    }
    // check if password contains at least one number
    if (!/[0-9]/.test(password)) {
      setError('Password must contain at least one number');
      return;
    }
    // check if password contains at least one special character
    if (!/[!@#$%^&*]/.test(password)) {
      setError('Password must contain at least one special character');
      return;
    }
    if (token) {
      setIsSubmitting(true);
      mutate({ token, new_password: password });
    }
  };

  if (isFetching) {
    return <div>Loading...</div>;
  }

  return (
    <Grid
      container
      direction={'column'}
      alignItems="center"
      sx={{ verticalAlign: 'middle' }}
    >
      <Grid item xs={12} p={5}>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title="Reset Password" sx={{ p: 1 }} />
          <CardContent sx={{ p: 1 }}>
            <Grid
              container
              spacing={2}
              direction={'column'}
              alignItems="center"
              sx={{ verticalAlign: 'middle' }}
            >
              <Grid
                item
                xs={12}
                textAlign={'center'}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <Image
                  src={logo}
                  alt="Logo"
                  sx={{ maxWidth: '300px' }}
                  width={'40vw'}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">
                  Enter your new password below.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption">
                  Password must be at least 12 characters long and contain at
                  least one uppercase letter, one lowercase letter, one number,
                  and one special character.
                </Typography>
              </Grid>
            </Grid>
            {!data ? (
              <Grid item xs={12} paddingTop={2} textAlign={'center'} gap={2}>
                <Typography variant="body1" color="error" sx={{ mb: 2 }}>
                  Invalid or expired reset link.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => (window.location.href = '/login')}
                  size="small"
                >
                  <ArrowBack />
                  Go to Login
                </Button>
              </Grid>
            ) : (
              <>
                <Grid item xs={12} paddingTop={2}>
                  <TextField
                    label="New Password"
                    variant="outlined"
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    disabled={disabled}
                    size={'small'}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleTogglePasswordVisibility}
                            edge="end"
                            disabled={disabled}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} paddingTop={2}>
                  <TextField
                    label="Confirm Password"
                    variant="outlined"
                    fullWidth
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    disabled={disabled}
                    size={'small'}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={handleToggleConfirmPasswordVisibility}
                            edge="end"
                            disabled={disabled}
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} paddingTop={2} textAlign={'center'}>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    size="small"
                    loading={isSubmitting}
                    disabled={isSubmitting || disabled}
                  >
                    Update Password
                  </LoadingButton>
                </Grid>
              </>
            )}
            <Grid item xs={12} paddingTop={2}>
              {error && (
                <AlertItem
                  severity="error"
                  description={error}
                  onClose={() => setError(null)}
                />
              )}
              {showAlert && (
                <AlertItem
                  severity="success"
                  description={
                    <Link href="/login" underline="hover">
                      Password updated successfully. Click here to login.
                    </Link>
                  }
                  onClose={() => setShowAlert(false)}
                  keepOpen={true}
                />
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ResetPassword;
